import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import { MatSelect, MAT_SELECT_SCROLL_STRATEGY } from '@angular/material/select';
import { RoutingMatSelectOverlayHelper } from 'src/app/routing-ruleconfig1/utils/routing-mat-select-overlay.util';
import { RuleEngineService } from '../../services/rule-engine.service';
import { Subscription } from 'rxjs';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Rule } from '../models/rule-engine';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';

/** Parsed node for manual expressions (rule numbers, names, and/or). */
interface ManualExprNode {
  type: 'num' | 'ref' | 'and' | 'or';
  value?: string;
  children?: ManualExprNode[];
}

export function expressionBuilderMatSelectScrollStrategyFactory(
  overlay: Overlay
): () => ScrollStrategy {
  return () => overlay.scrollStrategies.reposition();
}

@Component({
  selector: 'app-expression-builder',
  templateUrl: './expression-builder.component.html',
  styleUrls: ['./expression-builder.component.scss'],
  providers: [
    {
      provide: MAT_SELECT_SCROLL_STRATEGY,
      deps: [Overlay],
      useFactory: expressionBuilderMatSelectScrollStrategyFactory,
    },
  ],
})
export class ExpressionBuilderComponent implements OnInit, OnDestroy {
  private readonly matSelectOverlay = new RoutingMatSelectOverlayHelper();
  private _ruleServiceSubscription!: Subscription;
  private _ruleEngineService!: RuleEngineService;
  rules: Rule[] = [];
  private _fb!: FormBuilder;
  expressionBuilderForm!: FormGroup;
  finalExpression!: string;
  invalidExpression!: string;
  enableRoutingManualExpressionInput: boolean = false;
  manualExpressionText: string = '';

  private _manualTok: string[] = [];
  private _manualTi = 0;

  constructor(
    private toastr: ToastrService,
    fb: FormBuilder,
    ruleEngineService: RuleEngineService,
    public dialogRef: MatDialogRef<ExpressionBuilderComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this._fb = fb;
    this._ruleEngineService = ruleEngineService;
  }

  ngOnInit(): void {
    this.enableRoutingManualExpressionInput = !!this.data?.enableRoutingManualExpressionInput;
    this.manualExpressionText = (this.data?.manualExpression ?? '').toString();
    this._ruleServiceSubscription = this._ruleEngineService.getAllRules().subscribe({
      next: (data) => {
        this.rules = data;
      },
      error: (err) => {this.toastr.error("Unable to get rules", "", {
        timeOut: 4000,
        positionClass: 'toast-bottom-right'
      });}
    });

    this.initiateExpressoinBuilderForm();
    this.buildFinalExpression();
    let storedFormData = this._ruleEngineService.getTheBuilderExpressionData();
    if (storedFormData) {
      this.loadFormData(storedFormData);
    }
    if (this.enableRoutingManualExpressionInput && this.manualExpressionText.trim()) {
      // Keep exact persisted expression text in the preview bar on reopen.
      this.finalExpression = this.manualExpressionText.trim();
    }
  }

  ngOnDestroy(): void {
    this.matSelectOverlay.destroy();
    if (this._ruleServiceSubscription) {
      this._ruleServiceSubscription.unsubscribe();
    }
  }
  onClickClose() {
    this._ruleEngineService.setPopUpValueForExpressionBuilder(false);
    this.dialogRef.close({ confirmed: false });
  }

  onMatSelectOpenedChange(opened: boolean, select: MatSelect): void {
    this.matSelectOverlay.onOpenedChange(opened, select);
  }

  initiateExpressoinBuilderForm() {
    this.expressionBuilderForm = this._fb.group({
      expressionGroupArray: this._fb.array([
        this._fb.group({
          expressionArray: this._fb.array([
            this._expressionRow(
              '',
              this.rules.length ? this.rules[0].rulename : '',
              true
            ),
          ]),
          groupOperator: this._fb.control('', Validators.required),
        }),
      ]),
    });
    this.expressionBuilderForm.valueChanges.subscribe({
      next: (item: any) => { 
        this.buildFinalExpression()
        if(!this.validateExpressionBuilderForm()){
          this.invalidExpression = "Please fill all the fields in the form";
        }
        else{
          this.invalidExpression = "";
        }
      }
    });
  }

  get isRemoveDisabled(): boolean {
    return this.getExpressionGroupArray()?.length <= 1;
  }
  getExpressionGroupArray() {
    return this.expressionBuilderForm.get('expressionGroupArray') as FormArray;
  }
  getExpressionArray(expressionGroup: any) {
    return expressionGroup.get('expressionArray') as FormArray;
  }
  addExpression(expressionGroupIndex: number) {
    const expressionGroup = (
      this.expressionBuilderForm.get('expressionGroupArray') as FormArray
    ).at(expressionGroupIndex);
    const expressions = expressionGroup.get('expressionArray') as FormArray;
    expressions.push(this._addExpression());
  }

  _addExpression() {
    return this._fb.group({
      condition: this._fb.control('', Validators.required),
      rule: this._fb.control('', Validators.required),
    });
  }

  /** First row in each group keeps condition blank — no validators on that control. */
  private _expressionRow(condition: string, rule: string, isFirstInGroup: boolean) {
    return this._fb.group({
      condition: this._fb.control(condition, isFirstInGroup ? [] : Validators.required),
      rule: this._fb.control(rule, Validators.required),
    });
  }

  onClickAddGroup() {
    const expressionGroups = this.expressionBuilderForm.get(
      'expressionGroupArray'
    ) as FormArray;
    expressionGroups.push(this._addGroup());
  }

  onClickRemoveGroup(expressionGroupIndex: number) {
    const expressionGroups = this.expressionBuilderForm.get(
      'expressionGroupArray'
    ) as FormArray;
    expressionGroups.removeAt(expressionGroupIndex);
  }

  _addGroup() {
    return this._fb.group({
      expressionArray: this._fb.array([
        this._expressionRow('', this.rules?.length ? this.rules[0].rulename : '', true),
      ]),
      groupOperator: this._fb.control('',Validators.required),
    });
  }
  removeAnExpresion(expressionIndex: number, expressionGroupIndex: number) {
    const expressionsOfAGroup = (
      this.expressionBuilderForm.get('expressionGroupArray') as FormArray
    ).at(expressionGroupIndex);
    const expressionArray = expressionsOfAGroup.get('expressionArray') as FormArray;
    if (!this.canRemoveExpressionFromGroup(expressionArray)) {
      return;
    }
    expressionArray.removeAt(expressionIndex);
    this.clearConditionOnFirstRow(expressionArray);
  }

  /** First row must not carry AND/OR — it is hidden in the UI but can linger after a remove. */
  private clearConditionOnFirstRow(expressionArray: FormArray): void {
    if (!expressionArray?.length) {
      return;
    }
    const firstRow = expressionArray.at(0) as FormGroup;
    const condition = firstRow.get('condition');
    if (condition?.value) {
      condition.setValue('');
    }
  }

  canRemoveExpressionFromGroup(expressionArray: FormArray): boolean {
    return (expressionArray?.length ?? 0) > 1;
  }

  buildFinalExpression() {
    const formJson = this.expressionBuilderForm.value['expressionGroupArray'];
    if (!formJson?.length) {
      this.finalExpression = '';
      return;
    }
    const buildRange = (start: number, end: number): string => {
      const parts: string[] = [];
      for (let i = start; i <= end; i++) {
        const group = formJson[i];
        const innerExpression = (group?.expressionArray || [])
          .map((exp: any, rowIdx: number) =>
            `${rowIdx > 0 && exp.condition ? exp.condition + ' ' : ''}${exp.rule}`
          )
          .join(' ')
          .trim();
        parts.push(`(${innerExpression})`);
        if (i < end && group?.groupOperator) {
          parts.push(group.groupOperator);
        }
      }
      return parts.join(' ').trim();
    };

    this.finalExpression = buildRange(0, formJson.length - 1);
  }
  onClickSubmitExpression() {
    let isValid = this.validateExpressionBuilderForm();
    if (isValid) {
      this._ruleEngineService.holdTheBuildExpressionData = {
        ...this.expressionBuilderForm.value,
      };
      this._ruleEngineService.setFinalRuleExpression(this.finalExpression);
      this._ruleEngineService.setPopUpValueForExpressionBuilder(false);
      //let result = this.getAllRulesAsRuleExpresions();
      // let ruleExpressions: RuleExpressoins = {
      //   rules: result,
      //   ruleSatisfaction: this.finalExpression,
      // };
      this.dialogRef.close({
        confirmed: true,
        finalExpression: (this.finalExpression || '').toString().trim(),
        builderData: this._ruleEngineService.holdTheBuildExpressionData,
      });
      this.toastr.success("Rule expression updated", "", {
        timeOut: 500,
        positionClass: 'toast-bottom-right'
      });
    }
    else{
      this.invalidExpression = "Please fill all the fields in the form";
      this.toastr.warning("Please fill the required fileds", "", {
        timeOut: 4000,
        positionClass: 'toast-bottom-right'
      });}
    }

  onClickApplyManualExpression(): void {
    const normalized = (this.manualExpressionText || '')
      .toString()
      .replace(/\bAND\b/gi, 'and')
      .replace(/\bOR\b/gi, 'or')
      .replace(/\s+/g, ' ')
      .trim();

    if (!normalized) {
      this.toastr.warning('Enter build expression (example: 1 or (2 and 3)).', '', {
        timeOut: 3000,
        positionClass: 'toast-bottom-right'
      });
      return;
    }

    const validationError = this.validateManualExpression(normalized);
    if (validationError) {
      this.toastr.error(validationError, '', {
        timeOut: 4000,
        positionClass: 'toast-bottom-right'
      });
      return;
    }

    const top = this.splitTopLevelSegments(normalized);
    let groups: { rows: { condition: string; rule: string }[]; groupOperator: string }[];

    if (top.segments.length > 1) {
      groups = [];
      for (let idx = 0; idx < top.segments.length; idx++) {
        const seg = (top.segments[idx] || '').trim();
        if (!seg) {
          continue;
        }
        const partAst = this.parseManualExpressionAST(seg);
        if (!partAst) {
          this.toastr.error(
            'Could not parse this expression — check parentheses and AND/OR.',
            '',
            { timeOut: 4000, positionClass: 'toast-bottom-right' }
          );
          return;
        }
        const rows = this.manualAstToFormRows(partAst);
        if (rows.length) {
          groups.push({
            rows,
            groupOperator: idx < top.operators.length ? top.operators[idx] : '',
          });
        }
      }
    } else {
      const ast = this.parseManualExpressionAST(
        top.segments.length === 1 ? top.segments[0].trim() : normalized
      );
      if (!ast) {
        this.toastr.error(
          'Could not parse this expression — check parentheses and AND/OR.',
          '',
          { timeOut: 4000, positionClass: 'toast-bottom-right' }
        );
        return;
      }
      groups = this.manualAstToFormGroups(ast);
    }

    if (!groups?.length) {
      this.toastr.error('Expression did not yield any valid groups.', '', {
        timeOut: 4000,
        positionClass: 'toast-bottom-right',
      });
      return;
    }

    this.replaceVisualBuilderGroups(groups);
    this._ruleEngineService.holdTheBuildExpressionData = {
      ...this.expressionBuilderForm.value,
    };

    this.finalExpression = normalized;
    this._ruleEngineService.setFinalRuleExpression(this.finalExpression);

    this.toastr.success('Builder updated from your expression.', '', {
      timeOut: 2200,
      positionClass: 'toast-bottom-right',
    });
  }

  /** Rebuild visual builder with groups inferred from manual expression AST. */
  private replaceVisualBuilderGroups(
    groupsData: { rows: { condition: string; rule: string }[]; groupOperator: string }[]
  ): void {
    const groups = this.getExpressionGroupArray();
    while (groups.length) {
      groups.removeAt(groups.length - 1);
    }

    groupsData.forEach((groupData) => {
      const expressionArray = new FormArray<any>([]);
      groupData.rows.forEach((row, idx) =>
        expressionArray.push(this._expressionRow(row.condition, row.rule, idx === 0))
      );
      groups.push(
        this._fb.group({
          expressionArray,
          groupOperator: this._fb.control(groupData.groupOperator),
        })
      );
    });
  }

  private canonicalRulename(token: string): string {
    const t = (token || '').trim();
    const m = this.rules?.find(
      (r) => (r.rulename || '').trim().toLowerCase() === t.toLowerCase()
    );
    return (m?.rulename ?? '').trim() || t;
  }

  private ruleNameForNumber(numStr: string): string {
    const n = Number(numStr);
    const match = this.rules?.find((x: Rule) => Number(x?.rulenumber ?? 0) === n);
    if (match) {
      return (match.rulename ?? '').trim() || `Rule${numStr}`;
    }
    if (this.rules?.length && n >= 1 && n <= this.rules.length) {
      const fallback = this.rules[n - 1];
      return (fallback?.rulename ?? '').trim() || `Rule${numStr}`;
    }
    return `Rule${numStr}`;
  }

  private manualAstToFormRows(node: ManualExprNode): { condition: string; rule: string }[] {
    if (node.type === 'num') {
      return [{ condition: '', rule: this.ruleNameForNumber(node.value!) }];
    }
    if (node.type === 'ref') {
      return [{ condition: '', rule: this.canonicalRulename(node.value!) }];
    }
    const kids = node.children || [];
    if (kids.length < 2) {
      return [];
    }
    const opLabel = node.type === 'and' ? 'AND' : 'OR';
    return this.mergeManualFormRows(
      opLabel,
      this.manualAstToFormRows(kids[0]),
      this.manualAstToFormRows(kids[1])
    );
  }

  /**
   * Build visual-builder groups from AST:
   * - `(1 and 2) or 3` => Group1:[1 AND 2], operator OR, Group2:[3]
   * - If expression is a flat chain of same operator numbers, keep one group.
   */
  private manualAstToFormGroups(
    node: ManualExprNode
  ): { rows: { condition: string; rule: string }[]; groupOperator: string }[] {
    if (node.type === 'num' || node.type === 'ref') {
      return [{ rows: this.manualAstToFormRows(node), groupOperator: '' }];
    }

    const rootOp = node.type;
    const rootOpLabel = rootOp === 'and' ? 'AND' : 'OR';
    const topTerms = this.collectSameOperatorTerms(node, rootOp);

    // For "1 and 2 and 3" (or all OR), keep compact single group.
    if (topTerms.every((term) => term.type === 'num' || term.type === 'ref')) {
      return [{ rows: this.manualAstToFormRows(node), groupOperator: '' }];
    }

    const groups = topTerms
      .map((term) => ({
        rows: this.manualAstToFormRows(term),
        groupOperator: '',
      }))
      .filter((g) => g.rows.length > 0);

    for (let i = 0; i < groups.length - 1; i++) {
      groups[i].groupOperator = rootOpLabel;
    }
    return groups;
  }

  private collectSameOperatorTerms(node: ManualExprNode, op: 'and' | 'or'): ManualExprNode[] {
    if (node.type !== op || !node.children || node.children.length < 2) {
      return [node];
    }
    const out: ManualExprNode[] = [];
    for (const child of node.children) {
      out.push(...this.collectSameOperatorTerms(child, op));
    }
    return out;
  }

  /**
   * Split at parens depth 0 on and/or — same logic as routing when restoring saved expressions.
   */
  private splitTopLevelSegments(expr: string): {
    segments: string[];
    operators: ('AND' | 'OR')[];
  } {
    const segments: string[] = [];
    const operators: ('AND' | 'OR')[] = [];
    let depth = 0;
    let start = 0;
    let i = 0;

    while (i < expr.length) {
      const c = expr[i];
      if (c === '(') {
        depth++;
        i++;
        continue;
      }
      if (c === ')') {
        depth--;
        i++;
        continue;
      }
      if (depth === 0) {
        const rest = expr.slice(i);
        const opMatch = rest.match(/^\s*\b(or|and)\b/i);
        if (opMatch) {
          const word = opMatch[1].toLowerCase();
          const op: 'AND' | 'OR' = word === 'or' ? 'OR' : 'AND';
          segments.push(expr.slice(start, i).trim());
          operators.push(op);
          i += opMatch[0].length;
          start = i;
          continue;
        }
      }
      i++;
    }

    segments.push(expr.slice(start).trim());
    return { segments, operators };
  }

  private mergeManualFormRows(
    operator: string,
    left: { condition: string; rule: string }[],
    right: { condition: string; rule: string }[]
  ): { condition: string; rule: string }[] {
    if (!left.length) {
      return right;
    }
    if (!right.length) {
      return left;
    }
    return [...left, { condition: operator, rule: right[0].rule }, ...right.slice(1)];
  }

  /**
   * Tokenize manual expression: longest rulename match first so multi-word names work.
   */
  private lexRuleExpression(
    expr: string,
    rulenames: string[]
  ): string[] | null {
    const names = [...new Set(rulenames.map((s) => (s || '').trim()).filter(Boolean))].sort(
      (a, b) => b.length - a.length
    );
    const tokens: string[] = [];
    let i = 0;
    const n = expr.length;

    while (i < n) {
      while (i < n && /\s/.test(expr[i])) {
        i++;
      }
      if (i >= n) {
        break;
      }

      const c = expr[i];
      if (c === '(') {
        tokens.push('(');
        i++;
        continue;
      }
      if (c === ')') {
        tokens.push(')');
        i++;
        continue;
      }

      const rest = expr.slice(i);

      const kw = rest.match(/^(and|or)\b/i);
      if (kw) {
        tokens.push(kw[1].toLowerCase());
        i += kw[0].length;
        continue;
      }

      const num = rest.match(/^(\d+)/);
      if (num) {
        tokens.push(num[1]);
        i += num[1].length;
        continue;
      }

      let matched = false;
      for (const nm of names) {
        if (rest.length < nm.length) {
          continue;
        }
        if (rest.slice(0, nm.length).toLowerCase() === nm.toLowerCase()) {
          const boundary = rest[nm.length];
          if (boundary === undefined || /\s|[()]/.test(boundary)) {
            tokens.push(nm);
            i += nm.length;
            matched = true;
            break;
          }
        }
      }
      if (matched) {
        continue;
      }

      const id = rest.match(/^([\w.-]+)/);
      if (id) {
        tokens.push(id[1]);
        i += id[1].length;
        continue;
      }

      return null;
    }

    return tokens;
  }

  private parseManualExpressionAST(expr: string): ManualExprNode | null {
    const names = (this.rules || []).map((r) => r.rulename ?? '');
    const tokens = this.lexRuleExpression(expr, names);
    if (!tokens?.length) {
      return null;
    }
    this._manualTok = tokens;
    this._manualTi = 0;
    try {
      const node = this.parseManualOrLevel();
      if (this._manualTi !== tokens.length) return null;
      return node;
    } catch {
      return null;
    }
  }

  private parseManualOrLevel(): ManualExprNode {
    let left = this.parseManualAndLevel();
    while (
      this._manualTi < this._manualTok.length &&
      this._manualTok[this._manualTi].toLowerCase() === 'or'
    ) {
      this._manualTi++;
      const right = this.parseManualAndLevel();
      left = { type: 'or', children: [left, right] };
    }
    return left;
  }

  private parseManualAndLevel(): ManualExprNode {
    let left = this.parseManualPrimary();
    while (
      this._manualTi < this._manualTok.length &&
      this._manualTok[this._manualTi].toLowerCase() === 'and'
    ) {
      this._manualTi++;
      const right = this.parseManualPrimary();
      left = { type: 'and', children: [left, right] };
    }
    return left;
  }

  private parseManualPrimary(): ManualExprNode {
    const t = this._manualTok[this._manualTi++];
    if (t === '(') {
      const inner = this.parseManualOrLevel();
      if (this._manualTok[this._manualTi++] !== ')') {
        throw new Error('expected )');
      }
      return inner;
    }
    if (/^\d+$/.test(t)) {
      return { type: 'num', value: t };
    }
    return { type: 'ref', value: t };
  }

  private validateManualExpression(expression: string): string | null {
    let balance = 0;
    for (const ch of expression) {
      if (ch === '(') {
        balance++;
      }
      if (ch === ')') {
        balance--;
      }
      if (balance < 0) {
        return 'Expression brackets are not balanced.';
      }
    }
    if (balance !== 0) {
      return 'Expression brackets are not balanced.';
    }

    const names = (this.rules || []).map((r) => r.rulename ?? '');
    const tokens = this.lexRuleExpression(expression, names);
    if (tokens === null) {
      return 'Expression contains invalid characters or unrecognized tokens.';
    }
    if (!tokens.length) {
      return 'Enter an expression using rule numbers or Rule Names, with and/or.';
    }

    const savedTok = this._manualTok;
    const savedTi = this._manualTi;
    this._manualTok = tokens;
    this._manualTi = 0;
    let ast: ManualExprNode | null = null;
    try {
      ast = this.parseManualOrLevel();
      if (this._manualTi !== tokens.length) {
        return 'Could not parse expression — check parentheses and operators.';
      }
    } catch {
      return 'Could not parse expression — check syntax.';
    } finally {
      this._manualTok = savedTok;
      this._manualTi = savedTi;
    }

    const availableRuleNumbers = new Set<number>(
      (this.rules || [])
        .map((r: Rule, idx: number) => Number(r?.rulenumber ?? idx + 1))
        .filter((n: number) => Number.isFinite(n) && n > 0)
    );

    const visit = (n: ManualExprNode): string | null => {
      if (n.type === 'num') {
        const num = Number(n.value);
        if (!availableRuleNumbers.has(num)) {
          return `Rule number ${n.value} is not available in this rule set.`;
        }
        return null;
      }
      if (n.type === 'ref') {
        const raw = (n.value ?? '').trim();
        const ok = (this.rules || []).some(
          (r) => (r.rulename || '').trim().toLowerCase() === raw.toLowerCase()
        );
        if (!ok) {
          return `Unknown rule name "${raw}". Use names from the Rule Name list or numbers.`;
        }
        return null;
      }
      if (n.children) {
        for (const c of n.children) {
          const err = visit(c);
          if (err) {
            return err;
          }
        }
      }
      return null;
    };

    return ast ? visit(ast) : 'Could not parse expression.';
  }

  validateExpressionBuilderForm(): boolean {
    // Get the expressionGroupArray from the form
    const expressionGroupArray = this.expressionBuilderForm.get(
      'expressionGroupArray'
    ) as FormArray;

    let isValid = true; // Flag to track overall validity

    // Traverse each group in the expressionGroupArray
    for (let i = 0; i < expressionGroupArray.length; i++) {
      const group = expressionGroupArray.at(i) as FormGroup;

      // Get the expressionArray from the current group
      const expressionArray = group.get('expressionArray') as FormArray;

      // Traverse each expression in the expressionArray
      for (let j = 0; j < expressionArray.length; j++) {
        const expression = expressionArray.at(j) as FormGroup;

        // Get condition and rule from the current expression
        const condition = expression.get('condition');
        const rule = expression.get('rule');

        // Skip validation only for the first condition
        if (j === 0 && condition) {
          condition.markAsTouched(); // Mark condition as touched, but skip validation
        } else {
          // Validate all other conditions
          if (condition) {
            condition.markAsTouched(); // Mark condition as touched
            if (condition.invalid) {
              isValid = false; // If invalid, set flag to false
            }
          }
        }

        // Validate the rule field for all expressions
        if (rule) {
          rule.markAsTouched(); // Mark rule as touched
          const ruleValue = rule.value;
          const isRuleValid = this.rules.some(r => r.rulename === ruleValue);
          
          if (!isRuleValid) {
            rule.setErrors({ required: true }); // ❗️ Marks as invalid (same as required)
            isValid = false;
          } else {
            rule.setErrors(null); // Clears the error if valid
          }
          if (rule.invalid) {
            isValid = false; // If invalid, set flag to false
          }
        }
      }

      // Validate groupOperator
      const groupOperator = group.get('groupOperator');
      if (groupOperator) {
        groupOperator.markAsTouched(); // Mark groupOperator as touched
        if (i !== expressionGroupArray.length - 1) {
          // Validate groupOperator for all groups except the last one
          if (groupOperator.invalid) {
            isValid = false; // If invalid, set flag to false
          }
        }
      }
    }
    // Return overall validity
    return isValid;
  }

  // Method to load stored data into the form
  loadFormData(storedData: any) {
    const expressionGroupArray = this.expressionBuilderForm.get(
      'expressionGroupArray'
    ) as FormArray;

    if (storedData?.expressionGroupArray) {
      storedData.expressionGroupArray.forEach(
        (groupData: any, index: number) => {
          // Ensure that the FormArray has enough groups
          if (expressionGroupArray.length <= index) {
            expressionGroupArray.push(this._addGroup()); // Add new group if not enough
          }

          // Get the FormGroup from the FormArray
          const expressionGroup = expressionGroupArray.at(index) as FormGroup;

          // Patch expressionArray (nested FormArray) if it exists in the data
          if (groupData?.expressionArray) {
            this.patchExpressionArray(
              expressionGroup.get('expressionArray') as FormArray,
              groupData.expressionArray
            );
          }

          // Patch other fields of the group (like groupOperator)
          expressionGroup.patchValue({
            groupOperator: groupData.groupOperator,
          });
        }
      );
    }
  }

  // Helper method to patch FormArray data
  patchExpressionArray(expressionArray: FormArray, expressionArrayData: any[]) {
    // Clear current form array
    while (expressionArray.length) {
      expressionArray.removeAt(0);
    }

    // Add new controls based on the data
    expressionArrayData.forEach((data: any, idx: number) => {
      expressionArray.push(
        this._expressionRow(data.condition ?? '', data.rule ?? '', idx === 0)
      );
    });
  }

  filterRules(value: string): Rule[] {
    const filterValue = value.toLowerCase();
    return this.rules.filter(rule => 
      rule.rulename.toLowerCase().includes(filterValue)
    );
  }
}
